// components/layout/Header.tsx
import React, { useState } from 'react';
import { NavigateFunction, useLocation } from 'react-router-dom';
import { Toolbar, Tooltip, IconButton, Container, useTheme } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { NavigationItem } from 'types/navigation';
import { UserType } from 'types/types';
import { Beta } from 'components/ui/Beta';
import { CopyBubble } from 'components/CopyBubble';

import 'components/layout/Header.css';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import { DesktopNavigation, MobileNavigation } from './HeaderNavigation';
import {
  MobileDrawer,
  NavLinksContainer,
  StyledAppBar,
  UserActionsContainer,
} from './HeaderStyles';
import { HeaderUserSection } from './HeaderUserMenu';
import { createUserMenuItems } from './headerMenuUtils';

interface HeaderProps {
  transparent?: boolean;
  className?: string;
  navigate: NavigateFunction;
  navigationItems: NavigationItem[];
  currentPath: string;
  sessionId?: string | null;
}

const Header: React.FC<HeaderProps> = (props: HeaderProps) => {
  const { user, logout } = useAuth();
  const { setSnack } = useAppState();
  const { transparent = false, className, navigate, navigationItems, sessionId } = props;
  const theme = useTheme();
  const location = useLocation();

  const name = user?.firstName || user?.email || '';

  // State for desktop dropdown menus
  const [dropdownAnchors, setDropdownAnchors] = useState<{
    [key: string]: HTMLElement | null;
  }>({});

  // State for mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<{
    [key: string]: boolean;
  }>({});

  // State for user menu
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const isAdmin = user?.isAdmin || false;

  const userMenuItems = createUserMenuItems(
    (user?.userType as UserType | undefined) || null,
    isAdmin,
    navigate,
    logout
  );

  // Helper function to check if current path matches navigation item
  const isCurrentPath = (item: NavigationItem): boolean => {
    if (!item.path) return false;
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  // Helper function to check if any child is current path
  const hasActiveChild = (item: NavigationItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child => isCurrentPath(child) || hasActiveChild(child));
  };

  // Desktop dropdown handlers
  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, itemId: string): void => {
    setDropdownAnchors(prev => ({ ...prev, [itemId]: event.currentTarget }));
  };

  const handleDropdownClose = (itemId: string): void => {
    setDropdownAnchors(prev => ({ ...prev, [itemId]: null }));
  };

  // Mobile accordion handlers
  const handleMobileToggle = (itemId: string): void => {
    setMobileExpanded(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = (): void => {
    setUserMenuAnchor(null);
  };

  const handleUserMenuAction = (item: {
    id: string;
    label: string;
    icon: React.ReactElement | null;
    action: () => void;
    group?: string;
  }): void => {
    if (item.group !== 'divider') {
      item.action();
      handleUserMenuClose();
    }
  };

  // Navigation handlers
  const handleNavigate = (path: string): void => {
    navigate(path.replace(/:.*$/, ''));
    setMobileOpen(false);
    // Close all dropdowns
    setDropdownAnchors({});
  };

  return (
    <StyledAppBar
      position="fixed"
      transparent={transparent}
      className={className}
      sx={{ overflow: 'hidden' }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Navigation Links - Desktop */}
          <NavLinksContainer>
            <DesktopNavigation
              navigationItems={navigationItems}
              dropdownAnchors={dropdownAnchors}
              mobileExpanded={mobileExpanded}
              userExists={Boolean(user)}
              isCurrentPath={isCurrentPath}
              hasActiveChild={hasActiveChild}
              onDropdownOpen={handleDropdownOpen}
              onDropdownClose={handleDropdownClose}
              onMobileToggle={handleMobileToggle}
              onNavigate={handleNavigate}
            />
          </NavLinksContainer>

          {/* User Actions Section */}
          <UserActionsContainer>
            <HeaderUserSection
              user={user}
              name={name}
              theme={theme}
              userMenuOpen={userMenuOpen}
              userMenuAnchor={userMenuAnchor}
              userMenuItems={userMenuItems}
              onLogin={() => navigate('/login')}
              onUserMenuOpen={handleUserMenuOpen}
              onUserMenuClose={handleUserMenuClose}
              onUserMenuAction={handleUserMenuAction}
            />

            {/* Mobile Menu Button */}
            <Tooltip title="Open Menu">
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ display: { md: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>

            {sessionId && (
              <CopyBubble
                tooltip="Copy link"
                color="inherit"
                aria-label="copy link"
                edge="end"
                sx={{
                  width: 36,
                  height: 36,
                  opacity: 1,
                  bgcolor: 'inherit',
                  '&:hover': { bgcolor: 'action.hover', opacity: 1 },
                }}
                content={`${window.location.origin}${window.location.pathname}?id=${sessionId}`}
                onClick={(): void => {
                  navigate(`${window.location.pathname}?id=${sessionId}`);
                  setSnack('Link copied!');
                }}
                size="large"
              />
            )}
          </UserActionsContainer>

          {/* Mobile Navigation Drawer */}
          <MobileDrawer
            variant="temporary"
            anchor="right"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
          >
            <MobileNavigation
              navigationItems={navigationItems}
              dropdownAnchors={dropdownAnchors}
              mobileExpanded={mobileExpanded}
              userExists={Boolean(user)}
              isCurrentPath={isCurrentPath}
              hasActiveChild={hasActiveChild}
              onDropdownOpen={handleDropdownOpen}
              onDropdownClose={handleDropdownClose}
              onMobileToggle={handleMobileToggle}
              onNavigate={handleNavigate}
            />
          </MobileDrawer>
        </Toolbar>
      </Container>
      <Beta
        sx={{ left: '-130px', '& .mobile': { right: '0px' } }}
        onClick={(): void => {
          navigate('/');
        }}
      />
    </StyledAppBar>
  );
};

export { Header };
